import { filterSearchableList } from './assetSummaryDetailsHelpers';
import { ExtractedPlant, ExtractedSite } from './summarySitePlantUtils';
import { CustomerLite } from '../Plants/CreatePlant';

export const countPinnedAssetsInMap = (
  mapByCustomer: Record<string, Record<string, unknown>>,
  customerId?: string
): number => {
  if (customerId) {
    return Object.keys(mapByCustomer[customerId] || {}).length;
  }
  return Object.values(mapByCustomer).reduce(
    (total, customerMap) => total + Object.keys(customerMap).length,
    0
  );
};

export const filterCustomersForSearch = (customers: CustomerLite[], customerSearch: string) =>
  filterSearchableList(customers || [], customerSearch, (c) => [c.customerName, c.soldTo]);

export const filterSitesForSummary = (
  extractedSites: ExtractedSite[],
  selectedCustomerId: string,
  siteSearch: string
) => {
  let sitesToShow = extractedSites;
  if (selectedCustomerId) {
    sitesToShow = extractedSites.filter((site) => site.customerId === selectedCustomerId);
  }
  return filterSearchableList(sitesToShow, siteSearch, (site) => [
    site.site,
    site.siteId,
    site.customerName,
  ]);
};

export const filterPlantsForSummary = (
  extractedPlants: ExtractedPlant[],
  selectedSiteId: string,
  selectedCustomerId: string,
  plantSearch: string
) => {
  let plantsToShow = extractedPlants;
  if (selectedSiteId) {
    plantsToShow = extractedPlants.filter((plant) => plant.siteId === selectedSiteId);
  } else if (selectedCustomerId) {
    plantsToShow = extractedPlants.filter((plant) => plant.customerId === selectedCustomerId);
  }
  return filterSearchableList(plantsToShow, plantSearch, (plant) => [
    plant.plantName,
    plant.plantId,
    plant.siteName,
  ]);
};

export const rowMatchesAssetType = (row: Record<string, unknown>, selectedAssetType: string): boolean => {
  const selectedType = String(selectedAssetType).toLowerCase();
  const typeName = row.assetTypeName ? String(row.assetTypeName).toLowerCase() : '';
  const assetType = row.assetType ? String(row.assetType).toLowerCase() : '';
  return typeName === selectedType || assetType === selectedType;
};

export const filterRowsByAssetType = <T extends Record<string, unknown>>(rows: T[], selectedAssetType: string) => {
  if (!selectedAssetType) return rows;
  return rows.filter((row) => rowMatchesAssetType(row, selectedAssetType));
};

export const rowHasAlerts = (row: Record<string, unknown>): boolean => {
  const alertsCount = parseInt(String(row.alertsCount ?? ''), 10) || 0;
  const hasAlerts = Array.isArray(row.alerts) && row.alerts.length > 0;
  return hasAlerts || alertsCount > 0;
};

export const filterRowsByAlertsOnly = <T extends Record<string, unknown>>(rows: T[], showAlertsOnly: boolean) => {
  if (!showAlertsOnly) return rows;
  return rows.filter((row) => rowHasAlerts(row));
};

export const filterPinnedRowsByDropdowns = (
  pinnedRows: Record<string, unknown>[],
  filters: {
    selectedCustomerId: string;
    selectedSiteId: string;
    selectedPlantId: string;
    selectedAssetType: string;
    showAlertsOnly: boolean;
  }
) => {
  let filtered = pinnedRows;
  const { selectedCustomerId, selectedSiteId, selectedPlantId, selectedAssetType, showAlertsOnly } = filters;

  if (selectedCustomerId) {
    filtered = filtered.filter((row) => row.customerId === selectedCustomerId);
  }
  if (selectedSiteId) {
    filtered = filtered.filter((row) => row.siteId === selectedSiteId);
  }
  if (selectedPlantId) {
    filtered = filtered.filter((row) => row.plantId === selectedPlantId);
  }
  filtered = filterRowsByAssetType(filtered, selectedAssetType);
  return filterRowsByAlertsOnly(filtered, showAlertsOnly);
};

export const filterPinnedRowsBySearch = (rows: Record<string, unknown>[], searchText: string) => {
  if (!searchText?.trim()) return rows;
  const searchLower = searchText.toLowerCase();
  return rows.filter((row) =>
    ['customerName', 'siteName', 'plantName', 'assetType', 'assetName', 'status'].some((field) => {
      const value = row[field];
      return value && String(value).toLowerCase().includes(searchLower);
    })
  );
};

export const isSummaryDownloadDisabled = (
  selectedCustomerId: string,
  activeTab: 'assets' | 'pinned' | 'mapview',
  totalCount: number,
  pinnedTotalCount: number
) =>
  Boolean(selectedCustomerId) &&
  ((activeTab === 'assets' && totalCount === 0) || (activeTab === 'pinned' && pinnedTotalCount === 0));

export const getNextSortDirection = (
  current: { key: string; direction: 'asc' | 'desc' } | null,
  key: string
): 'asc' | 'desc' => {
  if (current?.key === key && current.direction === 'asc') return 'desc';
  return 'asc';
};
